import axios, { AxiosRequestConfig } from 'axios'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { log } from "@/services"
import { ApiConfig, Authorization } from "@/types"

const _api = axios.create()

export const useApi = defineStore('api', {
    actions: {
        async get(path: string, apiConfig: ApiConfig, authorization: Authorization, additionalHeaders: object = {}, params?: object) {
            const securityMetadata = generateSecurityMetadata(apiConfig.consumerKey)
            const url = apiConfig.baseUrl + path
            const config = {
                headers: {
                    'X-Security': securityMetadata,
                    Authorization: `${authorization.tokenType} ${authorization.accessToken}`,
                    Scope: apiConfig.scope,
                    ...additionalHeaders
                }
            } as AxiosRequestConfig
            if (params) {
                config.params = params
            }

            return _api.get(url, config)
                .then(response => {
                    log.trace(`${response.config.method} ${response.config.url}`)
                    return Promise.resolve(response)
                })
                .catch(error => {
                    log.warn('error: ', error)
                    if (error.response) {
                        log.info(`${error.response.request.responseURL}:`)
                        log.warn(`Response HTTP ${error.response.status} ${error.response.statusText}`)
                        globalThis.alert(`${error.response.status} : ${error.response.statusText}`)
                        error.intercepted = true;
                    }
                    return Promise.reject(error)
                })
        },
        async post(path: string, apiConfig: ApiConfig, authorization: Authorization, additionalHeaders: object = {}, data?: object) {
            const securityMetadata = generateSecurityMetadata(apiConfig.consumerKey)
            const url = apiConfig.baseUrl + path
            const config = {
                headers: {
                    'X-Security': securityMetadata,
                    Authorization: `${authorization.tokenType} ${authorization.accessToken}`,
                    Scope: apiConfig.scope,
                    ...additionalHeaders
                }
            } as AxiosRequestConfig
            return _api.post(url, data, config)
                .then(response => {
                    log.trace(`${response.config.method} ${response.config.url}`)
                    return Promise.resolve(response)
                })
                .catch(error => {
                    log.warn('error: ', error)
                    if (error.response) {
                        log.info(`${error.response.request.responseURL}:`)
                        log.warn(`Response HTTP ${error.response.status} ${error.response.statusText}`)
                        globalThis.alert(`${error.response.status} : ${error.response.statusText}`)
                        error.intercepted = true;
                    }
                    return Promise.reject(error)
                })
        },
        async request(
            method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD' | 'OPTIONS',
            url: string,
            apiConfig?: ApiConfig,
            authorization?: Authorization,
            additionalHeaders: object = {},
            data?: object,
            processData: boolean = false,
            contentType: false | string = false,
        ) {
            const config = { headers: additionalHeaders,  processData, contentType } as AxiosRequestConfig
            if (apiConfig) {
                const securityMetadata = generateSecurityMetadata(apiConfig.consumerKey)
                config.headers!['X-Security'] = securityMetadata
                config.headers!.Scope = apiConfig.scope
            }
            if (authorization) {
                config.headers!.Authorization = `${authorization.tokenType} ${authorization.accessToken}`
            }
            if (data) {
                config.data = data
            }
            return _api.request({ method, url, ...config })
                .then(response => {
                    log.trace(`${response.config.method} ${response.config.url}`)
                    return Promise.resolve(response)
                })
                .catch(error => {
                    log.warn('error: ', error)
                    if (error.response) {
                        log.info(`${error.response.request.responseURL}:`)
                        log.warn(`Response HTTP ${error.response.status} ${error.response.statusText}`)
                        globalThis.alert(`${error.response.status} : ${error.response.statusText}`)
                        error.intercepted = true;
                    }
                    return Promise.reject(error)
                })
        },
    }
})

function generateSecurityMetadata(clientId: string): string {
    log.trace('api.generateSecurityMetadata')

    const timestamp = (Date.now() / 1000 | 0).toString()
    const nonceLength = 12
    const nonce = [...Array(Math.ceil(nonceLength / 10))].map(() => Math.random().toString(36).slice(2)).join('').slice(0, nonceLength)

    const securityMetadata = `client-id="${clientId}",timestamp="${timestamp}",nonce="${nonce}"`

    return securityMetadata
}

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useApi, import.meta.hot))
}
